/**
 * @fileoverview Avatar Preview Modal
 * @description Fullscreen modal to view profile pictures with zoom support and View Profile button
 */

import React, { useState } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Dimensions,
  StatusBar,
  Platform,
} from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import { Gesture, GestureDetector, GestureHandlerRootView } from "react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

interface AvatarPreviewModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Avatar URL to display */
  avatarUrl: string | null | undefined;
  /** User's full name (for fallback initials) */
  userName?: string;
  /** User ID for navigation */
  userId?: string;
  /** Called when modal is closed */
  onClose: () => void;
  /** Called when "View Profile" is pressed */
  onViewProfile?: (userId: string) => void;
}

/**
 * Get initials from name
 */
const getInitials = (name?: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * AvatarPreviewModal Component
 * Shows a fullscreen modal with the user's profile picture and zoom support
 */
export const AvatarPreviewModal: React.FC<AvatarPreviewModalProps> = ({
  visible,
  avatarUrl,
  userName,
  userId,
  onClose,
  onViewProfile,
}) => {
  const insets = useSafeAreaInsets();
  const [imageError, setImageError] = useState(false);

  // Animated values for pinch-to-zoom
  const scale = useSharedValue(1);
  const savedScale = useSharedValue(1);

  /**
   * Pinch gesture for zoom using new Gesture API
   */
  const pinchGesture = Gesture.Pinch()
    .onStart(() => {
      savedScale.value = scale.value;
    })
    .onUpdate((event) => {
      scale.value = Math.max(1, Math.min(savedScale.value * event.scale, 4));
    })
    .onEnd(() => {
      if (scale.value < 1.1) {
        scale.value = withSpring(1);
      }
    });

  /**
   * Animated style for the image
   */
  const animatedImageStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  /**
   * Reset zoom and close modal
   */
  const handleClose = () => {
    scale.value = withTiming(1, { duration: 200 });
    onClose();
  };

  /**
   * Handle view profile press
   */
  const handleViewProfile = () => {
    if (userId && onViewProfile) {
      handleClose();
      // Small delay to allow modal to close first
      setTimeout(() => {
        onViewProfile(userId);
      }, 300);
    }
  };

  // Image size (responsive)
  const imageSize = Math.min(SCREEN_WIDTH * 0.85, SCREEN_HEIGHT * 0.5);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      statusBarTranslucent
    >
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      
      <GestureHandlerRootView style={{ flex: 1 }}>
        {/* Blurred background */}
        <BlurView
          intensity={Platform.OS === "ios" ? 80 : 100}
          tint="dark"
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Close button */}
          <TouchableOpacity
            onPress={handleClose}
            style={{
              position: "absolute",
              top: insets.top + 16,
              right: 16,
              width: 44,
              height: 44,
              borderRadius: 22,
              backgroundColor: "rgba(0,0,0,0.5)",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
            accessibilityLabel="Sluiten"
            accessibilityRole="button"
          >
            <Ionicons name="close" size={28} color="#FFFFFF" />
          </TouchableOpacity>

          {/* User name label */}
          {userName && (
            <View
              style={{
                position: "absolute",
                top: insets.top + 20,
                left: 16,
                right: 70,
              }}
            >
              <Text
                style={{
                  color: "#FFFFFF",
                  fontSize: 18,
                  fontWeight: "600",
                }}
                numberOfLines={1}
              >
                {userName}
              </Text>
            </View>
          )}

          {/* Avatar Image with pinch-to-zoom */}
          <GestureDetector gesture={pinchGesture}>
            <Animated.View
              style={[
                {
                  width: imageSize,
                  height: imageSize,
                  borderRadius: imageSize / 2,
                  overflow: "hidden",
                  backgroundColor: "#7C3AED20",
                  borderWidth: 4,
                  borderColor: "#FFFFFF",
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.4,
                  shadowRadius: 16,
                  elevation: 16,
                },
                animatedImageStyle,
              ]}
            >
              {avatarUrl && !imageError ? (
                <Image
                  source={{ uri: avatarUrl }}
                  style={{ width: "100%", height: "100%" }}
                  contentFit="cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <View
                  style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#7C3AED",
                  }}
                >
                  <Text
                    style={{
                      color: "#FFFFFF",
                      fontSize: imageSize * 0.3,
                      fontWeight: "700",
                    }}
                  >
                    {getInitials(userName)}
                  </Text>
                </View>
              )}
            </Animated.View>
          </GestureDetector>

          {/* Zoom hint */}
          <Text
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: 12,
              marginTop: 16,
            }}
          >
            Knijp om in te zoomen
          </Text>

          {/* View Profile button */}
          {userId && onViewProfile && (
            <TouchableOpacity
              onPress={handleViewProfile}
              style={{
                position: "absolute",
                bottom: insets.bottom + 32,
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: "#7C3AED",
                paddingHorizontal: 24,
                paddingVertical: 14,
                borderRadius: 28,
                shadowColor: "#7C3AED",
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.3,
                shadowRadius: 8,
                elevation: 8,
              }}
              accessibilityLabel="Bekijk profiel"
              accessibilityRole="button"
            >
              <Ionicons name="person" size={20} color="#FFFFFF" />
              <Text
                style={{
                  color: "#FFFFFF",
                  fontSize: 16,
                  fontWeight: "600",
                  marginLeft: 8,
                }}
              >
                Bekijk Profiel
              </Text>
            </TouchableOpacity>
          )}
        </BlurView>
      </GestureHandlerRootView>
    </Modal>
  );
};

export default AvatarPreviewModal;
